import { useMemo, useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { ClipboardList, Loader2, ShieldCheck, AlertCircle, Lock } from "lucide-react";
import { formatDMY } from "@/lib/date";
import { TurnstileWidget, captchaEnabled } from "@/components/TurnstileWidget";
import { TimeField } from "@/components/TimeField";

const EMAIL_DOMAINS = ["myivp.com.my", "ivpaero.com"];

const inputClass =
  "w-full rounded-lg border border-slate-300 px-4 py-2.5 text-base outline-none transition-all focus:border-[#5C2D91] focus:ring-1 focus:ring-[#5C2D91]";
const labelClass = "mb-1.5 block text-sm font-semibold tracking-wide text-[#0F172A]";
const errorClass = "mt-1 flex items-center gap-1 text-xs font-medium text-red-500";

const emptyForm = {
  employee_name: "",
  company_email: "",
  department: "",
  vehicle: "",
  start_time: "",
  return_time: "",
  purpose: "",
  destination: "",
  passengers: "",
  licence_confirmed: false,
};

const toMinutes = (t) => {
  if (!t) return null;
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
};

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const nowHHMM = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

export const BookingForm = ({ vehicles, bookingDate, allBookedSlots, onSubmit, submitting, lockedName = "", lockedEmail = "" }) => {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [captchaToken, setCaptchaToken] = useState("");
  const captchaRef = useRef(null);

  // Prefill (and lock) identity fields from the signed-in Microsoft account so
  // every new booking is guaranteed to match the authenticated user.
  useEffect(() => {
    setForm((f) => ({
      ...f,
      employee_name: lockedName || f.employee_name,
      company_email: lockedEmail || f.company_email,
    }));
  }, [lockedName, lockedEmail]);

  const isToday = bookingDate === todayStr();

  const selectedVehicle = useMemo(
    () => vehicles.find((v) => v.name === form.vehicle),
    [vehicles, form.vehicle],
  );

  const setField = (name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((e) => ({ ...e, [name]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.employee_name.trim()) e.employee_name = "Employee name is required";

    const email = form.company_email.trim().toLowerCase();
    if (!email) {
      e.company_email = "Company email is required";
    } else {
      const domain = email.split("@")[1];
      if (!email.includes("@") || !EMAIL_DOMAINS.includes(domain)) {
        e.company_email = "Use your IVP email (@myivp.com.my or @ivpaero.com)";
      }
    }

    if (!form.department.trim()) e.department = "Department is required";
    if (!form.vehicle) e.vehicle = "Please select a vehicle";
    if (!bookingDate) e.booking_date = "Select a booking date above";
    if (!form.start_time) e.start_time = "Start time is required";
    if (!form.return_time) e.return_time = "Return time is required";

    // For today, block times that have already passed
    if (isToday && form.start_time && toMinutes(form.start_time) < toMinutes(nowHHMM())) {
      e.start_time = "That time has already passed today — pick a later time";
    }
    if (isToday && form.return_time && toMinutes(form.return_time) < toMinutes(nowHHMM())) {
      e.return_time = "That time has already passed today";
    }

    if (form.start_time && form.return_time) {
      if (toMinutes(form.return_time) <= toMinutes(form.start_time)) {
        e.return_time = "Return time must be after start time";
      }
    }

    if (!form.purpose.trim()) e.purpose = "Purpose is required";
    if (!form.destination.trim()) e.destination = "Destination is required";

    const pax = Number(form.passengers);
    if (!form.passengers || Number.isNaN(pax) || pax < 1) {
      e.passengers = "Enter number of passengers";
    } else if (selectedVehicle && pax > selectedVehicle.capacity) {
      e.passengers = `${selectedVehicle.name} allows max ${selectedVehicle.capacity} passengers`;
    }

    if (selectedVehicle?.requires_licence && !form.licence_confirmed) {
      e.licence_confirmed = "You must confirm a valid driving licence";
    }

    // Block submission on overlapping slots incl. 15-min return buffer (same vehicle + date)
    if (form.vehicle && form.start_time && form.return_time && !e.return_time) {
      const s = toMinutes(form.start_time);
      const en = toMinutes(form.return_time) + 15;
      const slots = allBookedSlots[form.vehicle] || [];
      const clash = slots.some((slot) => {
        const bs = toMinutes(slot.start_time.slice(0, 5));
        const be = toMinutes((slot.buffer_end || slot.return_time).slice(0, 5));
        return s < be && bs < en;
      });
      if (clash) {
        e.start_time = "This time overlaps an existing booking or its 15-minute return buffer";
      }
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    if (captchaEnabled && !captchaToken) {
      setErrors((e) => ({ ...e, captcha: "Please complete the human verification below" }));
      return;
    }
    onSubmit({
      employee_name: (lockedName || form.employee_name).trim(),
      company_email: (lockedEmail || form.company_email).trim().toLowerCase(),
      department: form.department.trim(),
      vehicle: form.vehicle,
      booking_date: bookingDate,
      start_time: form.start_time,
      return_time: form.return_time,
      purpose: form.purpose.trim(),
      destination: form.destination.trim(),
      passengers: Number(form.passengers),
      licence_confirmed: form.licence_confirmed,
      turnstile_token: captchaToken,
    });
    // Token is single-use; re-arm the widget for any retry.
    setCaptchaToken("");
    captchaRef.current?.reset();
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.1 }}
      className="relative overflow-hidden rounded-xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8"
      data-testid="booking-form-card"
    >
      <div className="absolute left-0 right-0 top-0 h-2 bg-[#5C2D91]" aria-hidden="true" />

      <div className="mb-6 flex items-center gap-2">
        <ClipboardList className="h-5 w-5 text-[#5C2D91]" />
        <h2 className="font-heading text-lg font-semibold text-[#0F172A] sm:text-xl">
          Booking Details
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5" noValidate data-testid="booking-form">
        <div>
          <label htmlFor="employee_name" className={labelClass}>Employee name</label>
          <div className="relative">
            <input
              id="employee_name"
              type="text"
              value={lockedName || form.employee_name}
              readOnly={!!lockedName}
              onChange={(e) => { if (!lockedName) setField("employee_name", e.target.value); }}
              placeholder="Offar Musa"
              className={`${inputClass} ${lockedName ? "cursor-not-allowed bg-slate-50 pr-9 text-[#334155]" : ""}`}
              data-testid="input-employee-name"
            />
            {lockedName && <Lock className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#94a3b8]" />}
          </div>
          {errors.employee_name ? (
            <p className={errorClass} data-testid="error-employee-name">
              <AlertCircle className="h-3.5 w-3.5" />{errors.employee_name}
            </p>
          ) : lockedName ? (
            <p className="mt-1 text-xs text-[#64748B]">From your Microsoft account</p>
          ) : null}
        </div>

        <div>
          <label htmlFor="company_email" className={labelClass}>Company email</label>
          <div className="relative">
            <input
              id="company_email"
              type="email"
              value={lockedEmail || form.company_email}
              readOnly={!!lockedEmail}
              onChange={(e) => { if (!lockedEmail) setField("company_email", e.target.value); }}
              placeholder="name@myivp.com.my"
              className={`${inputClass} ${lockedEmail ? "cursor-not-allowed bg-slate-50 pr-9 text-[#334155]" : ""}`}
              data-testid="input-company-email"
            />
            {lockedEmail && <Lock className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#94a3b8]" />}
          </div>
          {errors.company_email ? (
            <p className={errorClass} data-testid="error-company-email">
              <AlertCircle className="h-3.5 w-3.5" />{errors.company_email}
            </p>
          ) : (
            <p className="mt-1 text-xs text-[#64748B]">{lockedEmail ? "From your Microsoft account" : "Must be @myivp.com.my or @ivpaero.com"}</p>
          )}
        </div>

        <div>
          <label htmlFor="department" className={labelClass}>Department</label>
          <input
            id="department"
            type="text"
            value={form.department}
            onChange={(e) => setField("department", e.target.value)}
            placeholder="e.g. Operations"
            className={inputClass}
            data-testid="input-department"
          />
          {errors.department && (
            <p className={errorClass} data-testid="error-department">
              <AlertCircle className="h-3.5 w-3.5" />{errors.department}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="vehicle" className={labelClass}>Vehicle</label>
          <select
            id="vehicle"
            value={form.vehicle}
            onChange={(e) => {
              setField("vehicle", e.target.value);
              setForm((f) => ({ ...f, licence_confirmed: false, passengers: "" }));
            }}
            className={`${inputClass} bg-white`}
            data-testid="select-vehicle"
          >
            <option value="">Select a vehicle…</option>
            {vehicles.map((v) => (
              <option key={v.id} value={v.name}>
                {v.name} (max {v.capacity} pax)
              </option>
            ))}
          </select>
          {errors.vehicle && (
            <p className={errorClass} data-testid="error-vehicle">
              <AlertCircle className="h-3.5 w-3.5" />{errors.vehicle}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="booking_date_display" className={labelClass}>Booking date</label>
          <input
            id="booking_date_display"
            type="text"
            value={formatDMY(bookingDate)}
            readOnly
            className={`${inputClass} bg-slate-50 text-[#64748B]`}
            data-testid="input-booking-date"
          />
          <p className="mt-1 text-xs text-[#64748B]">Change the date in the Availability section above.</p>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <label htmlFor="start_time" className={labelClass}>Start time</label>
            <TimeField
              id="start_time"
              value={form.start_time}
              onChange={(v) => setField("start_time", v)}
              testid="input-start-time"
            />
            {errors.start_time ? (
              <p className={errorClass} data-testid="error-start-time">
                <AlertCircle className="h-3.5 w-3.5" />{errors.start_time}
              </p>
            ) : (
              isToday && (
                <p className="mt-1 text-xs text-[#64748B]">Times earlier than now are unavailable today.</p>
              )
            )}
          </div>
          <div>
            <label htmlFor="return_time" className={labelClass}>Estimated return time</label>
            <TimeField
              id="return_time"
              value={form.return_time}
              onChange={(v) => setField("return_time", v)}
              testid="input-return-time"
            />
            {errors.return_time && (
              <p className={errorClass} data-testid="error-return-time">
                <AlertCircle className="h-3.5 w-3.5" />{errors.return_time}
              </p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="purpose" className={labelClass}>Purpose</label>
          <textarea
            id="purpose"
            rows={2}
            value={form.purpose}
            onChange={(e) => setField("purpose", e.target.value)}
            placeholder="e.g. Client site visit"
            className={`${inputClass} resize-none`}
            data-testid="input-purpose"
          />
          {errors.purpose && (
            <p className={errorClass} data-testid="error-purpose">
              <AlertCircle className="h-3.5 w-3.5" />{errors.purpose}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="destination" className={labelClass}>Destination</label>
          <input
            id="destination"
            type="text"
            value={form.destination}
            onChange={(e) => setField("destination", e.target.value)}
            placeholder="e.g. KLIA, Sepang"
            className={inputClass}
            data-testid="input-destination"
          />
          {errors.destination && (
            <p className={errorClass} data-testid="error-destination">
              <AlertCircle className="h-3.5 w-3.5" />{errors.destination}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="passengers" className={labelClass}>Number of passengers</label>
          <input
            id="passengers"
            type="number"
            min={1}
            max={selectedVehicle?.capacity || 8}
            value={form.passengers}
            onChange={(e) => setField("passengers", e.target.value)}
            placeholder={selectedVehicle ? `1 – ${selectedVehicle.capacity}` : "Select a vehicle first"}
            className={inputClass}
            data-testid="input-passengers"
          />
          {errors.passengers ? (
            <p className={errorClass} data-testid="error-passengers">
              <AlertCircle className="h-3.5 w-3.5" />{errors.passengers}
            </p>
          ) : (
            selectedVehicle && (
              <p className="mt-1 text-xs text-[#64748B]">
                {selectedVehicle.name} seats up to {selectedVehicle.capacity} passengers.
              </p>
            )
          )}
        </div>

        {selectedVehicle?.requires_licence && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className="rounded-lg border border-[#5C2D91]/30 bg-[#5C2D91]/5 p-4"
            data-testid="licence-block"
          >
            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                checked={form.licence_confirmed}
                onChange={(e) => setField("licence_confirmed", e.target.checked)}
                className="mt-0.5 h-5 w-5 rounded border-slate-300 text-[#5C2D91] focus:ring-[#5C2D91]"
                data-testid="checkbox-licence"
              />
              <span className="text-sm text-[#0F172A]">
                <span className="flex items-center gap-1.5 font-semibold">
                  <ShieldCheck className="h-4 w-4 text-[#5C2D91]" /> Driving licence confirmation
                </span>
                I confirm I hold a valid driving licence to operate the {selectedVehicle?.name}.
              </span>
            </label>
            {errors.licence_confirmed && (
              <p className={errorClass} data-testid="error-licence">
                <AlertCircle className="h-3.5 w-3.5" />{errors.licence_confirmed}
              </p>
            )}
          </motion.div>
        )}

        {captchaEnabled && (
          <div data-testid="booking-captcha">
            <TurnstileWidget
              ref={captchaRef}
              onToken={(t) => { setCaptchaToken(t); setErrors((e) => ({ ...e, captcha: undefined })); }}
              onExpired={() => setCaptchaToken("")}
            />
            {errors.captcha && (
              <p className={errorClass} data-testid="error-captcha">
                <AlertCircle className="h-3.5 w-3.5" />{errors.captcha}
              </p>
            )}
          </div>
        )}

        <div className="sticky bottom-0 z-20 -mx-5 border-t border-slate-100 bg-white/95 px-5 pb-3 pt-2 backdrop-blur sm:static sm:mx-0 sm:border-0 sm:bg-transparent sm:p-0 sm:backdrop-blur-none">
        <button
          type="submit"
          disabled={submitting}
          className="mt-2 flex w-full transform items-center justify-center gap-2 rounded-lg bg-[#5C2D91] px-4 py-3.5 font-semibold text-white shadow-sm transition-all hover:bg-[#4A2475] active:scale-[0.98] disabled:opacity-60"
          data-testid="submit-booking-button"
        >
          {submitting ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" /> Submitting…
            </>
          ) : (
            "Submit Booking"
          )}
        </button>
        </div>
      </form>
    </motion.section>
  );
};
